import { DateUtility } from '@utility/date';
import { theme } from '@utility/theme';
import { Warning } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { ShipmentTypeKeys } from '@utility/constants/common';
import {
  HeadingContainer,
  getProviderLabel,
} from '@page-components/timeline/common';
import { CommonUtility } from '@utility/common';
import { useInventoryStatusHook } from '@hooks/prediction';
import { NormalBarRenderer } from './row';

const red = '#FF4218';

export function ProvidersSection({
  filteredProviders,
  setFilteredProviders,
  differenceInDates,
  DayWidth,
  getBarColor,
  setMaxDate,
  setMinDate,
  data,
  activateVelocityWindow,
  hidePOPrediction,
  ...props
}) {
  const { checkEntry } = useInventoryStatusHook();

  useEffect(() => {
    if (!CommonUtility.isValidObject(data)) {
      setFilteredProviders([]);
      return;
    }

    const arr = [];

    let minDate = null;
    let maxDate = null;

    const shipments = [];
    const PO = [];

    for (let i = 0; i < data.purchase_orders?.length; i += 1) {
      const purchaseOrder = checkEntry(data.purchase_orders[i]);
      if (!purchaseOrder) continue;

      // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
      PO.push(purchaseOrder);
    }

    const shipmentsArray = [
      ...(data.shipments || []),
      ...(data.predicted_shipments || []),
    ];
    shipmentsArray.forEach(shipment => {
      const filteredShipment = checkEntry(shipment);
      if (
        filteredShipment &&
        !(
          shipment.status === 'prediction' &&
          shipment.origin_type === ShipmentTypeKeys.FP
        )
      ) {
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        shipments.push(filteredShipment);
      }
    });

    const gapBar = {
      title: 'Out Of Stock',
      link: '#',
      gap: true,
      color: red,
    };

    for (let i = 0; i < data.storageProviders?.length; i += 1) {
      if (
        data.storageProviders[i].unknownVelocityExists ||
        !data.storageProviders[i]?.fulfillment_provider_id ||
        data.storageProviders[i]?.is_primary_hybrid
      )
        continue;

      let providers = [];
      data.storageProviders[i].providers_data.forEach(provider => {
        const filtered = checkEntry(provider);
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        if (filtered) providers.push(filtered);
      });
      providers = providers.sort((a, b) => {
        // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
        if (DateUtility.isDateAfter(b.startDate, a.startDate)) {
          return -1;
        }
        // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
        if (DateUtility.isDateAfter(a.startDate, b.startDate)) {
          return 1;
        }

        return 0;
      });
      const depShipments = shipments.filter(
        // @ts-expect-error - TS2339: Property 'destination' does not exist on type 'never'.
        x => x.destination === data.storageProviders[i].fulfillment_provider_id,
      );
      if (providers.length < 1) {
        let outOfStockSP = {
          ...gapBar,
          _id: data.storageProviders[i].fulfillment_provider_id,
          startDate: new Date(),
          endDate: DateUtility.addDays(new Date(), 100),
          daysLeft: 0,
          infinite: true,
          name:
            data.storageProviders[i].fulfillment_provider_name || 'Placeholder',
          labelRenderer: getProviderLabel(data.storageProviders[i]),
          siblings: [],
        };
        const hasStock = data.storageProviders[i].present_inventory > 0;
        const isSystem = !CommonUtility.isNotNull(
          data.storageProviders[i].providerSalesVelocities?.manualSales?.value,
        );
        const isWarehouse = data.storageProviders[i].is_warehouse;
        const isFulfillable = data.storageProviders[i].is_fulfillable;
        if (hasStock && isSystem && !(isWarehouse && !isFulfillable)) {
          outOfStockSP = {
            ...outOfStockSP,
            gap: false,
            // @ts-expect-error - TS2322: Type 'Element' is not assignable to type 'string'.
            title: (
              <span className="sk:flex sk:items-center">
                <Warning className="sk:mr-1" size={18} /> Add Sales Velocity{' '}
              </span>
            ),
            className: 'add-sales-velocity',
            onClick: () =>
              activateVelocityWindow(
                data.storageProviders[i].fulfillment_provider_id,
              ),
          };
        } else if (
          hasStock &&
          isSystem &&
          data.storageProviders[i].providerSalesVelocity === 0
        ) {
          outOfStockSP = {
            ...outOfStockSP,
            gap: false,
            color: getBarColor(
              data.storageProviders[i]?.fulfillment_provider_id,
            ),
            className: 'sk:bg-transparent! sk:text-fg!',
            // @ts-expect-error - TS2322: Type 'Element' is not assignable to type 'string'.
            title: (
              <span className="sk:flex sk:items-center">
                Invalid Velocity - {data.storageProviders[i].present_inventory}{' '}
                Units In Stock
              </span>
            ),
            onClick: null,
          };
          const primaryWarehose =
            data.storageProviders.find(
              x => x.fulfillment_provider_id === data.primary_warehouse,
            ) || {};
          if (
            primaryWarehose.providerSalesVelocity &&
            !primaryWarehose.present_inventory
          ) {
            const daysLeft =
              data.storageProviders[i].present_inventory /
              primaryWarehose.providerSalesVelocity;
            const endDate = DateUtility.addDays(new Date(), daysLeft);
            outOfStockSP = checkEntry({
              ...outOfStockSP,
              startDate: new Date(),
              endDate,
              title: `${CommonUtility.roundNumberWithComma(
                daysLeft,
                1,
              )} Days of Stock`,
            });
            if (
              // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
              DateUtility.isDateAfter(outOfStockSP.endDate, maxDate) ||
              !maxDate
            ) {
              // @ts-expect-error - TS2322: Type 'Date' is not assignable to type 'null'.
              maxDate = outOfStockSP.endDate;
            }
          }
        }
        if (depShipments.length > 0) {
          depShipments.forEach(shipment => {
            outOfStockSP.infinite = false;
            // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
            outOfStockSP.endDate = shipment.endDate;

            // @ts-expect-error - TS2339: Property 'status' does not exist on type 'never'.
            if (shipment.status === 'prediction') {
              if (
                // @ts-expect-error - TS2339: Property 'status' does not exist on type 'never'.
                shipment.status === 'prediction' &&
                // @ts-expect-error - TS2339: Property 'from_prediction' does not exist on type 'never'.
                shipment.from_prediction &&
                !hidePOPrediction
              ) {
                const origin_id =
                  // @ts-expect-error - TS2339: Property 'origin_details' does not exist on type 'never'.
                  shipment?.origin_details?._id || shipment.origin_id;
                const relevantPO = data.order_predictions?.find(
                  x =>
                    x.status === 'prediction' && x.supplier?._id === origin_id,
                );
                if (relevantPO) {
                  const daysToShip = DateUtility.difference(
                    // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
                    new Date(shipment.startDate),
                    // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
                    new Date(shipment.endDate),
                  );
                  outOfStockSP.endDate = DateUtility.addDays(
                    outOfStockSP.endDate,
                    daysToShip,
                  );
                }
              }

              const lastSibling = outOfStockSP;
              const endDate = DateUtility.addDays(
                lastSibling.endDate,
                // @ts-expect-error - TS2339: Property 'qtyInDays' does not exist on type 'never'.
                shipment.qtyInDays,
              );
              const modSibling = checkEntry({
                startDate: DateUtility.addHours(lastSibling.endDate, 1),
                endDate,
                // @ts-expect-error - TS2339: Property 'qtyInDays' does not exist on type 'never'.
                daysLeft: shipment.qtyInDays,
                title: `${CommonUtility.numberWithCommas(
                  // @ts-expect-error - TS2339: Property 'qtyInDays' does not exist on type 'never'.
                  shipment.qtyInDays,
                )} Days of Predicted Stock`,
                link: '#',
                uniqueColor: theme.colors.yellow,
                className: 'dim',
              });
              if (modSibling) {
                if (
                  // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
                  DateUtility.isDateAfter(modSibling.endDate, maxDate) ||
                  !maxDate
                ) {
                  maxDate = modSibling.endDate;
                }

                // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
                outOfStockSP.siblings.push(modSibling);
              }
            }
          });
        }

        // @ts-expect-error - TS2345: Argument of type '{ _id: any; startDate: Date; endDate: Date; daysLeft: number; ...
        arr.push(outOfStockSP);
        continue;
      }

      let storageProvider = providers[0];
      if (!storageProvider) continue;

      // @ts-expect-error - TS2339: Property 'iteration' does not exist on type 'never'.
      const isDotted = storageProvider.iteration > 1;
      const daysStock = CommonUtility.numberWithCommas(
        // @ts-expect-error - TS2339: Property 'daysLeft' does not exist on type 'never'.
        Math.round(storageProvider.daysLeft) ||
          DateUtility.difference(
            // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
            storageProvider.startDate,
            // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
            storageProvider.endDate,
          ),
      );
      const titleStatus =
        // @ts-expect-error - TS2339: Property 'iteration' does not exist on type 'never'.
        storageProvider.iteration === 2 && !storageProvider.shipment_id
          ? '(Reserved)'
          : !isDotted
            ? '(Present Inventory)'
            : // @ts-expect-error - TS2339: Property 'shipment_display_id' does not exist on type 'never'.
              storageProvider?.shipment_display_id
              ? // @ts-expect-error - TS2339: Property 'shipment_display_id' does not exist on type 'never'.
                `(${storageProvider.shipment_display_id})`
              : '';

      // @ts-expect-error - TS2322: Type 'any' is not assignable to type 'never'.
      storageProvider = {
        // @ts-expect-error - TS2698: Spread types may only be created from object types.
        ...storageProvider,
        labelRenderer: getProviderLabel(data.storageProviders[i]),
        name:
          data.storageProviders[i].fulfillment_provider_name || 'Placeholder',
        _id: data.storageProviders[i].fulfillment_provider_id,
        link: `/app/scm/storage-provider/${data.storageProviders[i].fulfillment_provider_id}/`,
        color: getBarColor(data.storageProviders[i]?.fulfillment_provider_id),
        className: isDotted ? 'dim' : '',
        title: `${daysStock} Days of Stock ${titleStatus}`,
        siblings: [],
      };
      const shipmentDep = shipments.find(
        // @ts-expect-error - TS2339: Property 'origin_id' does not exist on type 'never'.
        x => x.origin_id === storageProvider._id,
      );
      if (shipmentDep) {
        // @ts-expect-error - TS2339: Property 'endConnect' does not exist on type 'never'.
        storageProvider.endConnect = shipmentDep._id;
      }

      if (
        DateUtility.isDateAfter(
          // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
          new Date(storageProvider.startDate),
          new Date(),
        ) &&
        DateUtility.difference(
          new Date(),
          // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
          new Date(storageProvider.startDate),
        ) > 0
      ) {
        // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
        storageProvider.siblings.push({
          startDate: new Date(),
          // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
          endDate: storageProvider.startDate,
          daysLeft: 0,
          ...gapBar,
        });
      }

      if (providers.length > 1) {
        for (let j = 1; j < providers.length; j += 1) {
          if (
            DateUtility.isDateAfter(
              // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
              providers[j - 1].endDate,
              // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
              providers[j].startDate,
            )
          ) {
            const daysLeft = DateUtility.difference(
              // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
              providers[j].startDate,
              // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
              providers[j].endDate,
            );
            // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
            providers[j].startDate = providers[j - 1].endDate;
            // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
            providers[j].endDate = DateUtility.addDays(
              // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
              providers[j - 1].endDate,
              daysLeft,
            );
          }

          const daysStock = CommonUtility.numberWithCommas(
            // @ts-expect-error - TS2339: Property 'daysLeft' does not exist on type 'never'.
            CommonUtility.roundNumber(providers[j].daysLeft, 1) ||
              DateUtility.difference(
                // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
                providers[j].startDate,
                // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
                providers[j].endDate,
              ),
          );
          const labelStatus =
            // @ts-expect-error - TS2339: Property 'iteration' does not exist on type 'never'.
            providers[j].iteration === 2 && !providers[j].shipment_id
              ? '(Reserved)'
              : // @ts-expect-error - TS2339: Property 'shipment_display_id' does not exist on type 'never'.
                providers[j]?.shipment_display_id
                ? // @ts-expect-error - TS2339: Property 'shipment_display_id' does not exist on type 'never'.
                  `(${providers[j].shipment_display_id})`
                : '';

          const sibling = checkEntry({
            // @ts-expect-error - TS2698: Spread types may only be created from object types.
            ...providers[j],
            // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
            startDate: DateUtility.addHours(providers[j].startDate, 1),
            className: 'dim',
            _id: data.storageProviders[i].fulfillment_provider_id,
            link: `/app/scm/storage-provider/${data.storageProviders[i].fulfillment_provider_id}/`,
            // @ts-expect-error - TS2339: Property 'color' does not exist on type 'never'.
            color: storageProvider.color,
            title: `${daysStock} Days of Stock ${labelStatus}`,
          });

          // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
          if (DateUtility.isDateAfter(sibling?.endDate, maxDate) || !maxDate) {
            maxDate = sibling?.endDate;
          }

          if (
            DateUtility.difference(
              // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
              new Date(providers[j - 1].endDate),
              // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
              new Date(providers[j].startDate),
              'hours',
            ) > 10
          ) {
            // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
            storageProvider.siblings.push({
              // @ts-expect-error - TS2554: Expected 2 arguments, but got 1.
              startDate: DateUtility.addHours(providers[j - 1].endDate),
              // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
              endDate: DateUtility.addHours(providers[j].startDate, 1),
              daysLeft: 0,
              ...gapBar,
            });
          }

          // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
          if (sibling) storageProvider.siblings.push(sibling);
        }
      } else if (depShipments.length) {
        // @ts-expect-error - TS2339: Property 'className' does not exist on type 'never'.
        storageProvider.className = 'dim';
      }

      // @ts-expect-error - TS2339: Property 'endConnect' does not exist on type 'never'.
      const depPO = PO.find(x => x.endConnect === storageProvider._id);
      if (depPO) {
        const toCompare =
          // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
          storageProvider.siblings.length > 0
            ? // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
              storageProvider.siblings[storageProvider.siblings.length - 1]
            : storageProvider;
        // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
        if (DateUtility.isDateAfter(depPO.endDate, toCompare.endDate)) {
          // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
          storageProvider.siblings.push({
            startDate: toCompare.endDate,
            // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
            endDate: DateUtility.addHours(depPO.endDate, 14),
            daysLeft: 0,
            ...gapBar,
          });
        }
      }

      if (depShipments.length > 0) {
        depShipments.forEach(shipment => {
          // @ts-expect-error - TS2339: Property 'status' does not exist on type 'never'.
          if (shipment.status === 'prediction') {
            const lastSibling =
              // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
              storageProvider.siblings.length > 0
                ? // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
                  storageProvider.siblings[storageProvider.siblings.length - 1]
                : storageProvider;
            // @ts-expect-error - TS2554: Expected 2 arguments, but got 1.
            let startDate = DateUtility.addHours(lastSibling.endDate);

            if (
              // @ts-expect-error - TS2339: Property 'status' does not exist on type 'never'.
              shipment.status === 'prediction' &&
              // @ts-expect-error - TS2339: Property 'from_prediction' does not exist on type 'never'.
              shipment.from_prediction &&
              !hidePOPrediction
            ) {
              const origin_id =
                // @ts-expect-error - TS2339: Property 'origin_details' does not exist on type 'never'.
                shipment?.origin_details?._id || shipment.origin_id;
              const relevantPO = data.order_predictions.find(
                x => x.status === 'prediction' && x.supplier?._id === origin_id,
              );
              if (relevantPO) {
                const daysToShip = DateUtility.difference(
                  // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
                  new Date(shipment.startDate),
                  // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
                  new Date(shipment.endDate),
                );
                startDate = DateUtility.addDays(
                  new Date(relevantPO.endDate),
                  daysToShip,
                );
              }
            }

            // @ts-expect-error - TS2339: Property 'qtyInDays' does not exist on type 'never'.
            const endDate = DateUtility.addDays(startDate, shipment.qtyInDays);

            const modSibling = checkEntry({
              startDate,
              endDate,
              // @ts-expect-error - TS2339: Property 'qtyInDays' does not exist on type 'never'.
              daysLeft: shipment.qtyInDays,
              title: `${CommonUtility.numberWithCommas(
                // @ts-expect-error - TS2339: Property 'qtyInDays' does not exist on type 'never'.
                CommonUtility.numberWithCommas(shipment.qtyInDays),
              )} Days of Predicted Stock`,
              link: '#',
              uniqueColor: theme.colors.yellow,
            });
            if (modSibling) {
              if (
                // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
                DateUtility.isDateAfter(modSibling.endDate, maxDate) ||
                !maxDate
              ) {
                maxDate = modSibling.endDate;
              }

              // @ts-expect-error - TS2339: Property 'siblings' does not exist on type 'never'.
              storageProvider.siblings.push(modSibling);
            }
          }
        });
      }

      arr.push(storageProvider);

      if (!minDate) {
        // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
        minDate = storageProvider.startDate;
      }

      // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
      if (!maxDate) maxDate = storageProvider.endDate;

      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(minDate, storageProvider.startDate))
        // @ts-expect-error - TS2339: Property 'startDate' does not exist on type 'never'.
        minDate = storageProvider.startDate;

      // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
      if (DateUtility.isDateAfter(storageProvider.endDate, maxDate)) {
        // @ts-expect-error - TS2339: Property 'endDate' does not exist on type 'never'.
        maxDate = storageProvider.endDate;
      }
    }

    shipments.forEach(shipment => {
      // @ts-expect-error - TS2339: Property 'destination_details' does not exist on type 'never'.
      if (shipment.destination_details?._id) {
        // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
        if (!arr?.find(x => x._id === shipment.destination_details?._id)) {
          // @ts-expect-error - TS2345: Argument of type '{ labelRenderer: JSX.Element; title: string; link: string; gap...
          arr.push({
            // @ts-expect-error - TS2339: Property 'destination_details' does not exist on type 'never'.
            _id: shipment.destination_details?._id,
            startDate: new Date(),
            endDate: DateUtility.addDays(new Date(), 100),
            daysLeft: 0,
            // @ts-expect-error - TS2339: Property 'destination_details' does not exist on type 'never'.
            name: shipment.destination_details?.name || 'Placeholder',
            ...gapBar,
            // @ts-expect-error - TS2339: Property 'destination_details' does not exist on type 'never'.
            labelRenderer: getProviderLabel(shipment.destination_details),
          });
        }
      }
    });

    setMinDate(minDate);
    setFilteredProviders(arr);
    setMaxDate(maxDate);
  }, [data]);

  return (
    <div className="sk:relative">
      <div
        className="sk:relative sk:z-120 sk:[border-top:1px_solid_var(--sk-hairline)]"
        style={{ width: differenceInDates * DayWidth + 208 }}
      >
        <HeadingContainer className="sk:flex sk:items-center">
          <h3 className="sk:m-0 sk:ml-6">Storage Providers</h3>
        </HeadingContainer>
      </div>
      {filteredProviders?.map((provider, i) => (
        // @ts-expect-error - TS2739: Type '{ name: any; getBarColor: any; data: any; context: string; last: number; h...
        <NormalBarRenderer
          key={provider?._id || `provider-${i}`}
          name={provider?.name}
          getBarColor={getBarColor}
          data={provider}
          context="provider"
          last={i === filteredProviders.length - 1 ? 1 : 0}
          headings={2}
          providers={filteredProviders}
          differenceInDates={differenceInDates}
          DayWidth={DayWidth}
          index={i}
          {...props}
        />
      ))}
    </div>
  );
}
